<!-- CareGraphAssignee.vue -->
<!--
  Reusable assignee control for a care-graph to-do. Resting state is an avatar
  chip (initials + name) or a faint "Assign" chip when unassigned; clicking opens
  a searchable picker popover (member list + Unassign). Emits assign/unassign —
  the parent persists. Used by the drawer Care Graph tab and the My Tasks list.
-->
<template>
  <div class="relative inline-block" @click.stop>
    <!-- Resting chip -->
    <button
      ref="trigger"
      type="button"
      class="inline-flex items-center gap-1.5 rounded-full transition-colors"
      :class="ownerName
        ? 'pl-0.5 pr-2 py-0.5 hover:bg-gray-100'
        : 'px-2 py-0.5 border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'"
      @click="toggle"
    >
      <template v-if="ownerName">
        <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-semibold text-white" :style="{ background: avatarColor(ownerName) }">
          {{ initials(ownerName) }}
        </span>
        <span class="text-xs text-gray-700 max-w-[110px] truncate">{{ ownerName }}</span>
      </template>
      <template v-else>
        <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[9px]" />
        <span class="text-[11px]">Assign</span>
      </template>
    </button>

    <!-- Picker popover — teleported to body with fixed positioning so it is
         never clipped by an ancestor's overflow (full chart or split drawer). -->
    <Teleport to="body">
    <div
      v-if="open"
      ref="popover"
      :style="popStyle"
      class="fixed z-[60] w-56 bg-white rounded-lg border border-gray-200 shadow-lg py-1"
    >
      <div class="px-2 pb-1">
        <input
          ref="search"
          v-model="query"
          type="text"
          placeholder="Search people or teams…"
          class="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500"
        />
      </div>
      <div class="max-h-56 overflow-y-auto">
        <button
          v-if="ownerEntityId"
          type="button"
          class="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gray-50 text-gray-500"
          @click="pick(null)"
        >
          <span class="inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300">
            <FontAwesomeIcon :icon="['fas', 'user-slash']" class="text-[9px] text-gray-400" />
          </span>
          <span class="text-xs">Unassign</span>
        </button>
        <button
          v-for="m in filteredMembers"
          :key="m.id"
          type="button"
          class="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gray-50"
          :class="{ 'bg-primary-50': m.id === ownerEntityId }"
          @click="pick(m.id)"
        >
          <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-semibold text-white" :style="{ background: avatarColor(m.name) }">
            {{ initials(m.name) }}
          </span>
          <span class="text-xs text-gray-700 flex-1 truncate">{{ m.name }}</span>
          <span v-if="m.kind === 'team'" class="text-[9px] font-semibold uppercase tracking-wide text-gray-400">Team</span>
          <FontAwesomeIcon v-if="m.id === ownerEntityId" :icon="['fas', 'check']" class="text-[10px] text-primary-600" />
        </button>
        <div v-if="!filteredMembers.length" class="px-2.5 py-2 text-[11px] text-gray-400">No members</div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const PALETTE = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

export default {
  name: 'CareGraphAssignee',
  components: { FontAwesomeIcon },
  props: {
    ownerEntityId: { type: Number, default: null },
    ownerName: { type: String, default: null },
    members: { type: Array, default: () => [] },   // [{id, name, kind?}]
  },
  emits: ['assign', 'unassign'],
  data() {
    return { open: false, query: '', popStyle: {} };
  },
  computed: {
    filteredMembers() {
      const q = this.query.toLowerCase().trim();
      if (!q) return this.members;
      return this.members.filter(m => (m.name || '').toLowerCase().includes(q));
    },
  },
  beforeUnmount() {
    this._teardown();
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.query = '';
        this.$nextTick(() => {
          this._position();
          this.$refs.search && this.$refs.search.focus();
        });
        document.addEventListener('click', this._onDocClick, true);
        window.addEventListener('scroll', this._position, true);
        window.addEventListener('resize', this._position, true);
      } else {
        this._teardown();
      }
    },
    _position() {
      const t = this.$refs.trigger;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const W = 224; // w-56
      const margin = 8;
      // Right-align the popover to the trigger, clamped to the viewport.
      let left = Math.min(r.right - W, window.innerWidth - W - margin);
      left = Math.max(margin, left);
      // Below by default; flip above if there isn't room.
      const belowSpace = window.innerHeight - r.bottom;
      const openUp = belowSpace < 260 && r.top > belowSpace;
      const style = { left: `${Math.round(left)}px`, width: `${W}px` };
      if (openUp) style.bottom = `${Math.round(window.innerHeight - r.top + 4)}px`;
      else style.top = `${Math.round(r.bottom + 4)}px`;
      this.popStyle = style;
    },
    pick(memberId) {
      if (memberId == null) this.$emit('unassign');
      else if (memberId !== this.ownerEntityId) this.$emit('assign', memberId);
      this.open = false;
      this._teardown();
    },
    _onDocClick(e) {
      // The popover is teleported to <body>, so check it explicitly in addition
      // to the trigger's own subtree.
      const inTrigger = this.$el.contains(e.target);
      const pop = this.$refs.popover;
      const inPop = pop && pop.contains(e.target);
      if (!inTrigger && !inPop) { this.open = false; this._teardown(); }
    },
    _teardown() {
      document.removeEventListener('click', this._onDocClick, true);
      window.removeEventListener('scroll', this._position, true);
      window.removeEventListener('resize', this._position, true);
    },
    initials(name) {
      if (!name) return '';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
    },
    avatarColor(name) {
      let h = 0;
      for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
      return PALETTE[h % PALETTE.length];
    },
  },
};
</script>
